import { AsyncPipe, NgClass } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  BbitArray,
  BbitLegalFilesResponse,
  BbitTime,
  BbitUtils,
  IBbitI18nSelectDataRow,
  IBbitLegalFile,
  IBbitPlaceBookingCalcResponse,
  IBbitPlaceBookingMetaResponse,
} from '@bbitgmbh/bbit.utils';
import { BbitWebShopFormatPricePipe } from '@bbitgmbh/webshop.sdk';
import { BbitOnlinePaymentService, BbitWebsiteService, I18nPipe, LuxonPipe } from '@bbitgmbh/website.sdk';
import { NgIcon } from '@ng-icons/core';
import { DateTime, Duration, Interval } from 'luxon';
import { BehaviorSubject, merge } from 'rxjs';
import { LoadingComponent } from '../../helper/loading/loading.component';
import { PlaceBookingService } from '../booking.service';
import { BookingOverlayRef, IBbitBookingOverlayData } from './booking-overlay.ref';

@Component({
  selector: 'app-booking-overlay',
  templateUrl: './booking-overlay.component.html',
  styleUrl: './booking-overlay.component.scss',
  imports: [
    NgIcon,
    LoadingComponent,
    ReactiveFormsModule,
    FormsModule,
    NgClass,
    AsyncPipe,
    LuxonPipe,
    BbitWebShopFormatPricePipe,
    I18nPipe,
  ],
})
export class BookingOverlayComponent {
  public data!: IBbitBookingOverlayData;
  private _onlinePayment = inject(BbitOnlinePaymentService);
  private _service = inject(PlaceBookingService);
  public displayType = new BehaviorSubject<'book' | 'success' | 'error' | 'loading' | 'redirectToPayment'>('loading');
  public usageType = new BehaviorSubject('');
  public canBeBooked = new BehaviorSubject(false);
  public durationRows = new BehaviorSubject<{ _id: string; text: string; interval: Interval }[]>([]);
  public selectedDuration = new BehaviorSubject<string | undefined>(undefined);
  private _allPaymentMethods: IBbitI18nSelectDataRow[] = [];
  public paymentMethods = new BehaviorSubject<IBbitI18nSelectDataRow[]>([]);
  public selectedPaymentMethod = new BehaviorSubject<string | undefined>(undefined);
  public isCalculatingPrice = new BehaviorSubject(true);
  public errorOnPriceCalculation = signal(false);
  public hasMissingValues = new BehaviorSubject(true);
  public price = new BehaviorSubject<IBbitPlaceBookingCalcResponse | undefined>(undefined);
  public meta = new BehaviorSubject<IBbitPlaceBookingMetaResponse | undefined>(undefined);
  public legalFiles = new BehaviorSubject<BbitLegalFilesResponse>([]);
  public acceptedLegalFiles = signal<string[]>([]);
  private _skipNextPriceCalculation = false;

  constructor(
    private _overlayRef: BookingOverlayRef,
    private _website: BbitWebsiteService,
  ) {
    this.data = this._overlayRef.getData();

    const getIntervalText = (interval: Interval): string => {
      const duration = interval.toDuration();
      return `${interval.start?.toFormat('HH:mm')} - ${interval.end?.toFormat('HH:mm')} (${duration.toFormat('h:mm')})`;
    };

    const rows: { _id: string; text: string; interval: Interval }[] = [];
    const baseInterval = Interval.fromDateTimes(this.data.event.from, this.data.event.until);
    const defaultId = BbitUtils.makeId();
    this.selectedDuration.next(defaultId);
    rows.push({
      _id: defaultId,
      text: getIntervalText(baseInterval),
      interval: baseInterval,
    });

    if (this.data.option.allowDurationChange) {
      const calendarEvents: { from: DateTime; until: DateTime }[] = this.data.events.filter(
        (o) => o.calendar._id === this.data.event.calendar._id,
      );

      // inject closing time
      calendarEvents.push({
        from: this.data.closingTime,
        until: this.data.closingTime.endOf('day'),
      });

      const nextEvent = BbitArray.sortBy(calendarEvents, (o) => o.from.toISO() || '').find(
        (o) => o.from.toMillis() >= this.data.event.until.toMillis(),
      );

      const upcomingInterval = Interval.fromDateTimes(
        this.data.event.until,
        nextEvent?.from || this.data.event.from.plus({ hours: 1 }),
      );

      const defaultDuration = this.data.option.duration || Duration.fromDurationLike({ minutes: 15 });
      const durations = upcomingInterval
        .splitBy(defaultDuration)
        .filter((o) => o.toDuration().as('minutes') === defaultDuration.as('minutes'));

      for (const o of durations) {
        const interval = Interval.fromDateTimes(this.data.event.from, o.end as DateTime);
        rows.push({
          _id: BbitUtils.makeId(),
          text: getIntervalText(interval),
          interval,
        });
      }
    }

    this.usageType.pipe(takeUntilDestroyed()).subscribe(() => {
      this._loadMeta();
    });

    toObservable(this.acceptedLegalFiles)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.canBeBooked.next(this._canBeBooked());
      });

    this.durationRows.next(rows);
    merge(this.usageType, this.selectedPaymentMethod, this.selectedDuration)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.canBeBooked.next(false);
        if (!this.selectedPaymentMethod.value) {
          return;
        }

        // if (!this.usageType.value) {
        //   return;
        // }

        this.calcPrice();
      });
  }

  private _canBeBooked(): boolean {
    if (this.errorOnPriceCalculation()) {
      return false;
    }
    if (this.acceptedLegalFiles().length !== this.legalFiles.value.length) {
      return false;
    }
    if (this.selectedPaymentMethod.value === '#credit') {
      const newCreditBalance = (this.meta.value?.creditBalance || 0) - (this.price.value?.credits || 0);
      if (newCreditBalance < 0) {
        return false;
      }
    }
    return true;
  }

  private async _loadLegalFiles(): Promise<void> {
    const files = await this._service.getLegalFiles();
    this.legalFiles.next(files);
  }

  async calcPrice(): Promise<void> {
    if (this._skipNextPriceCalculation) {
      this._skipNextPriceCalculation = false;
      return;
    }

    this.canBeBooked.next(false);
    this.isCalculatingPrice.next(true);

    if (!this.selectedDuration.value || !this.selectedPaymentMethod.value) {
      // || !this.usageType.value
      this.hasMissingValues.next(true);
      this.isCalculatingPrice.next(false);
      return;
    }
    this.hasMissingValues.next(false);

    const body = this._getBookingBody();

    try {
      const response = await fetch(`${this._website.getBlApiUrl()}/placeBooking/calc`, {
        method: 'POST',
        headers: {
          Authorization: `${this._website.getAccessToken()}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        this.errorOnPriceCalculation.set(true);
        throw new Error('Booking failed');
      }
      const result: IBbitPlaceBookingCalcResponse = await response.json();

      if (result.isLastMinute) {
      }
      this.errorOnPriceCalculation.set(false);
      this.canBeBooked.next(this._canBeBooked());
      this.price.next(result);
      this._updatePaymentMethods();
    } catch (err) {
      console.error(err);
      this.errorOnPriceCalculation.set(true);
      this.canBeBooked.next(this._canBeBooked());
      this.price.next(undefined);
    } finally {
      this.isCalculatingPrice.next(false);
    }
  }

  async ngOnInit(): Promise<void> {
    await Promise.all([this._loadMeta(), this._loadLegalFiles()]);
    this.displayType.next('book');
  }

  private async _loadMeta(): Promise<void> {
    const meta = await this._service.getMeta({ place: this.data.event.calendar._id, usageType: this.usageType.value });
    this.meta.next(meta);
    this._allPaymentMethods = meta.allowedPaymentMethods || [];
    this._updatePaymentMethods();
    this.calcPrice();
  }

  private _updatePaymentMethods(): void {
    let paymentMethods = this._allPaymentMethods;

    if (this.price.value?.isLastMinute) {
      paymentMethods = paymentMethods.filter((o) => o._id === '#online-payment');
    }

    this.paymentMethods.next(paymentMethods);

    if (!this.selectedPaymentMethod.value || !paymentMethods.find((o) => o._id === this.selectedPaymentMethod.value)) {
      this._skipNextPriceCalculation = true;
      this.selectedPaymentMethod.next(paymentMethods[0]?._id);
    }
  }

  close(reload = false) {
    this._overlayRef.close(reload);
  }

  // TODO types
  // biome-ignore lint/suspicious/noExplicitAny: required
  private _getBookingBody(): any {
    let from = BbitTime.fromDateTime(this.data.event.from).toISOTime();
    let until = BbitTime.fromDateTime(this.data.event.until).toISOTime();
    if (this.selectedDuration.value) {
      const duration = this.durationRows.value.find((o) => o._id === this.selectedDuration.value);
      from = BbitTime.fromDateTime(duration?.interval.start || this.data.event.from).toISOTime();
      until = BbitTime.fromDateTime(duration?.interval.end || this.data.event.until).toISOTime();
    }
    return {
      date: this.data.event.from,
      place: this.data.event.calendar._id,
      paymentMethod: this.selectedPaymentMethod.value,
      from,
      until,
      usageType: this.usageType.value,
    };
  }

  async book(): Promise<void> {
    if (!this.canBeBooked.value) {
      return;
    }
    this.displayType.next('loading');

    const body = this._getBookingBody();
    try {
      const response = await fetch(`${this._website.getBlApiUrl()}/placeBooking/book`, {
        method: 'POST',
        headers: {
          Authorization: `${this._website.getAccessToken()}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Booking failed');
      }
      const result = await response.json();

      if (result.isOnlinePayment) {
        if (result.invoiceId) {
          this._onlinePayment.pay({
            invoice: result.invoiceId,
            redirectUrl: `${window.location.origin}/shop/order-result`,
          });
          this.displayType.next('redirectToPayment');
          return;
        }
      }

      this.displayType.next('success');
    } catch (err) {
      this.displayType.next('error');
      console.error(err);
    }
  }

  public toggleAcceptedLegalFile(id: string): void {
    if (this.acceptedLegalFiles().includes(id)) {
      this.acceptedLegalFiles.set(this.acceptedLegalFiles().filter((o) => o !== id));
    } else {
      this.acceptedLegalFiles.set([...this.acceptedLegalFiles(), id]);
    }
  }

  public getDownloadUrl(file: IBbitLegalFile): string {
    return this._service.getDownloadUrl(file);
  }
}
